interface NavItemLike<TId extends string> {
  id: TId;
  href?: string;
}

interface NavSectionLike<TId extends string> {
  items: readonly NavItemLike<TId>[];
}

/**
 * Resolves the active sidebar item id for a given URL pathname.
 *
 * An item matches when the pathname equals its href or descends from it
 * (e.g. `/projects/123` matches `/projects`). When multiple items match,
 * the one with the longest href wins so nested routes don't fall back to
 * an ancestor section.
 */
export function resolveActiveNavId<TId extends string>(
  pathname: string,
  sections: readonly NavSectionLike<TId>[],
): TId | undefined {
  let bestId: TId | undefined;
  let bestLength = -1;

  for (const section of sections) {
    for (const item of section.items) {
      const { href } = item;
      if (!href) continue;
      const isMatch = pathname === href || pathname.startsWith(`${href}/`);
      if (!isMatch) continue;
      if (href.length > bestLength) {
        bestId = item.id;
        bestLength = href.length;
      }
    }
  }

  return bestId;
}
